import React, { useMemo, useState } from "react";
import type { HTMLComponent } from "~/types/HTMLComponent";
import type { WhoIsWovarAndWhyWovarSliceDuoContent } from "~/lib/prismic";

import WovarBrandTitle from "~/components/atoms/WvWovarSVGTitle";
import WvYoutubeVideoPlayer from "~/components/atoms/WvYoutubeVideoPlayer";
import { PrismicImage } from "@prismicio/react";
import { asText, asLink } from "@prismicio/helpers"
import { getYoutubeVideoID, getVimeoVideoID } from "@commercekit/shared";
import { useVideoSchema } from "~/lib/schema";

const WhoIsWovarVideo: HTMLComponent<{
  slice: WhoIsWovarAndWhyWovarSliceDuoContent;
}> = ({ slice }) => {
  const videoURL = useMemo(
    () => asLink(slice?.primary?.who_is_wovar_video_link),
    [slice?.primary?.who_is_wovar_video_link]
  );

  const isYoutubeVideo = useMemo(
    () =>
      slice?.primary?.who_is_wovar_video_type === "Youtube" ||
      (videoURL && /youtube.com/.test(videoURL)),
    [slice?.primary?.who_is_wovar_video_type, videoURL]
  );

  const videoID = isYoutubeVideo
    ? getYoutubeVideoID(videoURL)
    : getVimeoVideoID(videoURL);

  const embedLink = useMemo(
    () =>
      isYoutubeVideo
        ? `https://www.youtube.com/embed/${videoID}?autoplay=1&autohide=1&fs=1&rel=0&hd=1&wmode=transparent&enablejsapi=1&html5=1`
        : `https://player.vimeo.com/video/${videoID}?autoplay=1&hd=1&show_title=1&show_byline=1&show_portrait=0&fullscreen=1&api=1`,
    [videoID, isYoutubeVideo]
  );

  const embedUrlForSchema = useMemo(
    () =>
      isYoutubeVideo
        ? `https://www.youtube.com/embed/${videoID}`
        : `https://player.vimeo.com/video/${videoID}`,
    [videoID, isYoutubeVideo]
  );

  const videoSchemaParams = useMemo(
    () =>
      videoID
        ? {
            name: asText(slice?.primary?.who_is_wovar_title) || "Video",
            embedUrl: embedUrlForSchema,
            thumbnailUrl: slice?.primary?.who_is_wovar_cover_image?.url,
            description: asText(slice?.primary?.play_button_title) || undefined,
          }
        : null,
    [
      videoID,
      embedUrlForSchema,
      slice?.primary?.who_is_wovar_title,
      slice?.primary?.who_is_wovar_cover_image?.url,
      slice?.primary?.play_button_title,
    ]
  );

  useVideoSchema(`video-schema-${videoID ?? "who-is-wovar"}`, videoSchemaParams);

  const [showVideo, setShowVideo] = useState(false);

  return showVideo ? (
    <WvYoutubeVideoPlayer link={embedLink} />
  ) : (
    <div className="component-video" id="YoutubePlayer--utH6g7R_OY">
      <PrismicImage
        field={slice?.primary?.who_is_wovar_cover_image}
        loading="lazy"
        className="component-video__img"
      />
      <div
        className="component-video__overlay h-full w-full"
        data-href="-utH6g7R_OY"
      >
        <div className="d-flex flex-column justify-content-center align-items-center w-full h-full">
          <h2 className="heading xxl uppercase inverted mb-2 mb-lg-4 text-center">
            {asText(slice?.primary?.who_is_wovar_title)}
          </h2>
          <div className="mb-3 mb-lg-4">
            <WovarBrandTitle />
          </div>
          <button
            className="btn btn--ghost"
            type="button"
            data-icon-before=""
            onClick={() => setShowVideo(true)}
          >
            {asText(slice?.primary?.play_button_title)}
          </button>
        </div>
      </div>
    </div>
  );
};

const WhyWovar: HTMLComponent<{
  slice: WhoIsWovarAndWhyWovarSliceDuoContent;
}> = ({ slice }) => (
  <div className="component-usps h-full">
    <div className="component-usps__inner pb-3 py-xl-4 h-full">
      <div className="d-flex flex-wrap">
        <div className="col-12 col-xl-5 col-xxl-6 d-lg-flex flex-lg-column justify-content-lg-center">
          <PrismicImage
            field={slice.primary.why_wovar_image}
            loading="lazy"
            className="d-block mx-auto"
            width={350}
            height={350}
          />
        </div>
        <div className="col-12 col-xl-7 col-xxl-6 mt-n5 mt-lg-0">
          <div className="text-center">
            <div className="px-3 d-inline-block text-left">
              <h2 className="heading mdl underlined left pb-3 pb-lg-2 mb-3">
                {asText(slice?.primary?.why_wovar_title)}
              </h2>
              <ul>
                {slice.items.map((item, index) => (
                  <li
                    className="d-flex mb-3"
                    key={`${item?.why_wovar_item_description}_${index}`}
                  >
                    <span className="mr-3">
                      <svg
                        width="13"
                        height="12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.293.33A.923.923 0 0112.72 1.5l-.013.017-7.754 9.23a.923.923 0 01-1.38.04l-.013-.016L.237 7.08a.923.923 0 011.358-1.25l.014.015 2.613 2.903L11.293.33z"
                          fill="#E7AE23"
                          fillRule="nonzero"
                        ></path>
                      </svg>
                    </span>
                    <span>
                      <span className="d-block">
                        {asText(item?.why_wovar_item_title)}
                      </span>
                      <span>{asText(item?.why_wovar_item_description)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DuoContent: HTMLComponent<{
  slice: WhoIsWovarAndWhyWovarSliceDuoContent;
}> = ({ slice }) => (
  <div className="component-duocontent mb-3 mb-lg-5">
    <div className="container px-0">
      <div className="row no-gutters mx-0">
        <div className="col-12 col-lg-6 mb-3">
          <WhyWovar slice={slice} />
        </div>
        <div className="col-12 col-lg-6 mb-3">
          <WhoIsWovarVideo slice={slice} />
        </div>
      </div>
    </div>
  </div>
);

export default DuoContent;
